import type {nothing, TemplateResult} from 'lit';
import type {Model, Msg} from './architecture.js';
import type {ActionButtonKeys} from './actions.js';
import type {UpdateHandler} from '#lit/components/mixins/immutable_component/index.js';
import type {AudienceExport} from '#types/audiences/exports.js';

type ActionParams = {
  model: Model;
  option: ActionButtonOption;
  handleUpdate: UpdateHandler<Model, Msg>;
};

type RenderContentFn<T extends Partial<ActionParams> = object> = (
  model: T['model'] extends Model ? Model : never,
  option: T['option'] extends ActionButtonOption ? ActionButtonOption : never,
  handleUpdate: T['handleUpdate'] extends UpdateHandler<Model, Msg>
    ? UpdateHandler<Model, Msg>
    : never
) => TemplateResult | typeof nothing;

type HandleActionFn<T extends Partial<ActionParams> = object> = (
  model: T['model'] extends Model ? Model : never,
  option: T['option'] extends ActionButtonOption ? ActionButtonOption : never,
  handleUpdate?: T['handleUpdate'] extends UpdateHandler<Model, Msg>
    ? UpdateHandler<Model, Msg>
    : never
) => void;

export type ActionButtonOption = {
  key: ActionButtonKeys;
  label: string;
  order: number;
  getVisibility: (model: Model) => {
    visible: boolean;
    disabled: boolean;
  };
  renderContent: RenderContentFn<ActionParams>;
  handleAction?: HandleActionFn<ActionParams>;
};

export function createRenderFn<T extends Partial<ActionParams>>(
  fn: (params: T) => TemplateResult | typeof nothing
): RenderContentFn<T> {
  return (model, option, handleUpdate) =>
    fn({model, option, handleUpdate} as unknown as T);
}

export function createActionHandler<T extends Partial<ActionParams>>(
  fn: (params: T) => void
): HandleActionFn<T> {
  return (model, option, handleUpdate) =>
    fn({model, option, handleUpdate} as unknown as T);
}

export type OpenEditExportModalEventDetail = {
  audienceExport: Model['audienceExport'];
  destinationName: Model['destinationName'];
  destinationIcon: Model['destinationIcon'];
  frequencies: Model['frequencies'];
  label: ActionButtonOption['label'];
  isExpired: Model['isExpired'];
};

export type IngestModalEventDetail = {
  audienceExport: AudienceExport;
};
export const GLOBAL_AE_EVENT_KEYS = {
  EXPORT: 'global:ae:export',
  INGEST: 'global:ae:ingest',
  VIEW_INSIGHTS: 'global:ae:view-insights',
} as const;
